import { useState, useEffect } from 'react';
import { get, del, put } from '../../utils/api';
import { getUserLanguage } from '../../utils/language';

const OrdersEditor = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showOrderDetailsModal, setShowOrderDetailsModal] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderDetails, setOrderDetails] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [shippingMethods, setShippingMethods] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const language = getUserLanguage();

  const statusOptions = [
    { value: 'pending', label: language === 'cs' ? 'Čeká na zpracování' : 'Pending' },
    { value: 'processing', label: language === 'cs' ? 'Zpracovává se' : 'Processing' },
    { value: 'shipped', label: language === 'cs' ? 'Odesláno' : 'Shipped' },
    { value: 'completed', label: language === 'cs' ? 'Dokončeno' : 'Completed' },
    { value: 'done', label: language === 'cs' ? 'Dokončeno' : 'Done' },
    { value: 'cancelled', label: language === 'cs' ? 'Zrušeno' : 'Cancelled' },
  ];

  useEffect(() => {
    fetchOrders();
    fetchShippingMethods();
    fetchPaymentMethods();
  }, []);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    try {
      const url = '/orders/admin/orders';
      console.log('[OrdersEditor] Fetching from:', url);
      const headers = { 'accept-language': language };
      const response = await get(url, { headers });
      if (Array.isArray(response)) {
        setOrders(response);
      } else {
        throw new Error('Invalid data format from API');
      }
    } catch (err) {
      console.error('[OrdersEditor] Error fetching orders:', err);
      setError(
        language === 'cs'
          ? 'Nepodařilo se načíst objednávky: ' + (err.message || 'Není dostupná síť')
          : 'Failed to load orders: ' + (err.message || 'Network unavailable')
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchShippingMethods = async () => {
    try {
      const url = '/orders/shipping-methods';
      const headers = { 'accept-language': language };
      const response = await get(url, { headers });
      if (Array.isArray(response)) {
        setShippingMethods(response);
      }
    } catch (err) {
      console.error('[OrdersEditor] Error fetching shipping methods:', err);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const url = '/orders/payment-methods';
      const headers = { 'accept-language': language };
      const response = await get(url, { headers });
      if (Array.isArray(response)) {
        setPaymentMethods(response);
      }
    } catch (err) {
      console.error('[OrdersEditor] Error fetching payment methods:', err);
    }
  };

  const getMethodName = (methodId, methodsList) => {
    if (!methodId || !Array.isArray(methodsList)) return 'Neuvedeno';
    const method = methodsList.find(m => m.id.toString() === methodId.toString());
    return method ? method.name : 'Neuvedeno';
  };

  const getShippingMethodName = (methodId) => {
    return getMethodName(methodId, shippingMethods);
  };

  const getPaymentMethodName = (methodId) => {
    return getMethodName(methodId, paymentMethods);
  };

  const fetchOrderDetails = async (id) => {
    try {
      const url = `/orders/admin/orders/${id}`;
      const headers = { 'accept-language': language };
      const response = await get(url, { headers });
      setOrderDetails(response);
      return response;
    } catch (err) {
      console.error('[OrdersEditor] Error fetching order details:', err);
      setError(
        language === 'cs'
          ? 'Nepodařilo se načíst detail objednávky: ' + (err.message || '')
          : 'Failed to load order details: ' + (err.message || '')
      );
      throw err;
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'cs' ? 'Opravdu chcete smazat tuto objednávku?' : 'Are you sure you want to delete this order?')) {
      return;
    }
    try {
      const url = `/orders/admin/orders/${id}`;
      const headers = { 'accept-language': language };
      await del(url, { headers });
      setSuccess(language === 'cs' ? 'Objednávka úspěšně smazána' : 'Order deleted successfully');
      fetchOrders();
    } catch (err) {
      console.error('[OrdersEditor] Error deleting order:', err);
      setError(
        language === 'cs'
          ? 'Nepodařilo se smazat objednávku: ' + (err.message || '')
          : 'Failed to delete order: ' + (err.message || '')
      );
    }
  };

  const handleEdit = (order) => {
    setCurrentOrder(order);
    setNewStatus(order.status || 'pending');
    setShowEditModal(true);
  };

  const handleViewDetails = async (order) => {
    try {
      setCurrentOrder(order);
      await fetchOrderDetails(order.id);
      setShowOrderDetailsModal(true);
    } catch (err) {
      console.error('[OrdersEditor] Error in handleViewDetails:', err);
    }
  };

  const handleStatusChange = async () => {
    if (!currentOrder) return;
    try {
      const url = `/orders/admin/orders/${currentOrder.id}`;
      const data = { status: newStatus };
      await put(url, data);
      setSuccess(
        language === 'cs'
          ? `Status objednávky ${currentOrder.id} byl změněn na "${getStatusLabel(newStatus)}"`
          : `Order ${currentOrder.id} status changed to "${getStatusLabel(newStatus)}"`
      );
      setShowEditModal(false);
      fetchOrders();
    } catch (err) {
      console.error('[OrdersEditor] Error updating order status:', err);
      setError(
        language === 'cs'
          ? 'Nepodařilo se aktualizovat status objednávky: ' + (err.message || '')
          : 'Failed to update order status: ' + (err.message || '')
      );
    }
  };

  const handleDownloadInvoice = (id, type = 'proforma') => {
    const url = `/orders/admin/orders/${id}/invoice?type=${type}`;
    window.open(url, '_blank');
  };

  const getStatusLabel = (statusValue) => {
    const option = statusOptions.find((opt) => opt.value === statusValue);
    return option ? option.label : statusValue;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US');
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'completed':
      case 'done':
        return 'admin-badge-green';
      case 'cancelled':
        return 'admin-badge-red';
      case 'shipped':
        return 'admin-badge-blue';
      case 'processing':
        return 'admin-badge-purple';
      default:
        return 'admin-badge-yellow';
    }
  };

  const formatPrice = (price) => {
    if (!price) return '0 Kč';
    return parseFloat(price).toLocaleString(language === 'cs' ? 'cs-CZ' : 'en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' Kč';
  };

  const formatOrderId = (id) => {
    if (!id) return 'N/A';
    return '#' + id.toString().padStart(10, '0');
  };

  if (loading) {
    return <div className="admin-p-4">{language === 'cs' ? 'Načítání...' : 'Loading...'}</div>;
  }

  return (
    <div className="admin-p-4">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-6">
        <h1 className="admin-text-2xl admin-font-bold">
          {language === 'cs' ? 'Správa objednávek' : 'Orders Management'}
        </h1>
      </div>

      {error && <div className="admin-alert admin-alert-error admin-mb-4">{error}</div>}
      {success && <div className="admin-alert admin-alert-success admin-mb-4">{success}</div>}

      <div className="admin-card admin-bg-dark-gray">
        <div className="admin-card-header admin-bg-accent-gray">
          {language === 'cs' ? 'Seznam objednávek' : 'Orders List'}
        </div>
        {orders.length === 0 ? (
          <div className="admin-p-4 admin-text-center admin-text-gray-300">
            {language === 'cs' ? 'Žádné objednávky nebyly nalezeny.' : 'No orders found.'}
          </div>
        ) : (
          <div className="admin-overflow-x-auto">
            <table className="admin-table">
              <thead>
                <tr className="admin-bg-accent-gray">
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Objednávka' : 'Order'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Zákazník' : 'Customer'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Datum' : 'Date'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Celková cena' : 'Total'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Status' : 'Status'}</th>
                  <th className="admin-px-6 admin-py-3 admin-text-left">{language === 'cs' ? 'Akce' : 'Actions'}</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="admin-table-row">
                    <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                      <div className="admin-text-sm admin-text-white">{formatOrderId(order.id)}</div>
                    </td>
                    <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                      <div className="admin-text-sm admin-text-white">{order.customer_name}</div>
                      <div className="admin-text-xs admin-text-gray-400">{order.customer_email}</div>
                    </td>
                    <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                      <div className="admin-text-sm admin-text-white">{formatDate(order.created_at)}</div>
                    </td>
                    <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                      <div className="admin-text-sm admin-text-white">{formatPrice(order.total)}</div>
                    </td>
                    <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap">
                      <div className={`admin-badge ${getStatusClass(order.status)}`}>
                        {getStatusLabel(order.status)}
                      </div>
                    </td>
                    <td className="admin-px-6 admin-py-4 admin-whitespace-nowrap admin-text-sm admin-font-medium">
                      <button onClick={() => handleViewDetails(order)} className="admin-text-green-600 hover:admin-text-green-400 admin-mr-3">
                        {language === 'cs' ? 'Detail' : 'Details'}
                      </button>
                      <button onClick={() => handleEdit(order)} className="admin-text-blue-600 hover:admin-text-blue-400 admin-mr-3">
                        {language === 'cs' ? 'Upravit' : 'Edit'}
                      </button>
                      <button onClick={() => handleDelete(order.id)} className="admin-text-red-600 hover:admin-text-red-400">
                        {language === 'cs' ? 'Smazat' : 'Delete'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditModal && currentOrder && (
  <div className="admin-fixed admin-inset-0 admin-bg-black admin-bg-opacity-50 admin-flex admin-items-center admin-justify-center admin-z-50">
    <div className="admin-bg-dark-gray admin-rounded-lg admin-shadow-lg admin-w-full admin-max-w-xl admin-mx-auto admin-p-6">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-4">
        <h3 className="admin-text-xl admin-font-bold admin-text-white">
          {language === 'cs' ? `Upravit objednávku ${formatOrderId(currentOrder.id)}` : `Edit Order ${formatOrderId(currentOrder.id)}`}
        </h3>
        <button onClick={() => setShowEditModal(false)} className="admin-text-gray-400 hover:admin-text-white">
          <svg xmlns="http://www.w3.org/2000/svg" className="admin-h-6 admin-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      <div className="admin-mb-4">
        <div className="admin-text-sm admin-text-gray-400 admin-mb-1">{language === 'cs' ? 'Zákazník' : 'Customer'}:</div>
        <div className="admin-text-white">{currentOrder.customer_name}</div>
        <div className="admin-text-gray-300 admin-text-sm">{currentOrder.customer_email}</div>
        <div className="admin-text-gray-300 admin-text-sm">{currentOrder.customer_phone}</div>
        <div className="admin-text-gray-300 admin-text-sm">{currentOrder.customer_address}</div>
      </div>
      
      <div className="admin-grid admin-grid-cols-2 admin-gap-4 admin-mb-4">
        <div>
          <div className="admin-text-sm admin-text-gray-400 admin-mb-1">{language === 'cs' ? 'Doprava' : 'Shipping'}:</div>
          <div className="admin-text-white admin-font-medium">{getShippingMethodName(currentOrder.shipping_method)}</div>
        </div>
        <div>
          <div className="admin-text-sm admin-text-gray-400 admin-mb-1">{language === 'cs' ? 'Platba' : 'Payment'}:</div>
          <div className="admin-text-white admin-font-medium">{getPaymentMethodName(currentOrder.payment_method)}</div>
        </div>
      </div>
      
      <div className="admin-mb-4">
        <div className="admin-text-sm admin-text-gray-400 admin-mb-1">{language === 'cs' ? 'Celková částka' : 'Total Amount'}:</div>
        <div className="admin-text-white admin-font-bold">{formatPrice(currentOrder.total)}</div>
      </div>
      
      <div className="admin-mb-6">
        <label className="admin-block admin-text-sm admin-font-medium admin-text-gray-300 admin-mb-2">
          {language === 'cs' ? 'Status objednávky' : 'Order Status'}
        </label>
        <select
          value={newStatus}
          onChange={(e) => setNewStatus(e.target.value)}
          className="admin-form-select admin-block admin-w-full admin-rounded admin-border-gray-600 admin-bg-gray-700 admin-text-white admin-p-2"
        >
          {statusOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      
      <div className="admin-flex admin-justify-end admin-space-x-3">
        <button onClick={() => setShowEditModal(false)} className="admin-btn admin-btn-secondary">
          {language === 'cs' ? 'Zrušit' : 'Cancel'}
        </button>
        <button onClick={handleStatusChange} className="admin-btn admin-btn-primary">
          {language === 'cs' ? 'Uložit změny' : 'Save Changes'}
        </button>
      </div>
    </div>
  </div>
)}

{showOrderDetailsModal && currentOrder && orderDetails && (
  <div className="admin-fixed admin-inset-0 admin-bg-black admin-bg-opacity-50 admin-flex admin-items-center admin-justify-center admin-z-50">
    <div className="admin-bg-dark-gray admin-rounded-lg admin-shadow-lg admin-w-full admin-max-w-3xl admin-mx-auto admin-p-6">
      <div className="admin-flex admin-justify-between admin-items-center admin-mb-4">
        <h3 className="admin-text-xl admin-font-bold admin-text-white">
          {language === 'cs' ? `Detail objednávky ${formatOrderId(currentOrder.id)}` : `Order Details ${formatOrderId(currentOrder.id)}`}
        </h3>
        <button onClick={() => setShowOrderDetailsModal(false)} className="admin-text-gray-400 hover:admin-text-white">
          <svg xmlns="http://www.w3.org/2000/svg" className="admin-h-6 admin-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="admin-grid admin-grid-cols-1 admin-md:grid-cols-2 admin-gap-6 admin-mb-6">
        <div>
          <h4 className="admin-text-lg admin-font-semibold admin-text-white admin-mb-2">
            {language === 'cs' ? 'Zákazník' : 'Customer'}
          </h4>
          <div className="admin-bg-gray-800 admin-rounded admin-p-4">
            <p className="admin-font-medium admin-text-white">{orderDetails.customer_name}</p>
            <p className="admin-text-gray-300">{orderDetails.customer_email}</p>
            <p className="admin-text-gray-300">{orderDetails.customer_phone}</p>
            <p className="admin-text-gray-300">{orderDetails.customer_address}</p>
          </div>
        </div>
        <div>
          <h4 className="admin-text-lg admin-font-semibold admin-text-white admin-mb-2">
            {language === 'cs' ? 'Detaily objednávky' : 'Order Details'}
          </h4>
          <div className="admin-bg-gray-800 admin-rounded admin-p-4">
            <div className="admin-flex admin-justify-between admin-mb-2">
              <span className="admin-text-gray-300">{language === 'cs' ? 'Datum vytvoření' : 'Order Date'}:</span>
              <span className="admin-text-white">{formatDate(orderDetails.created_at)}</span>
            </div>
            <div className="admin-flex admin-justify-between admin-mb-2">
              <span className="admin-text-gray-300">{language === 'cs' ? 'Status' : 'Status'}:</span>
              <span className={`admin-badge ${getStatusClass(orderDetails.status)}`}>
                {getStatusLabel(orderDetails.status)}
              </span>
            </div>
            <div className="admin-flex admin-justify-between admin-mb-2">
              <span className="admin-text-gray-300">{language === 'cs' ? 'Způsob platby' : 'Payment Method'}:</span>
              <span className="admin-text-white">{getPaymentMethodName(orderDetails.payment_method)}</span>
            </div>
            <div className="admin-flex admin-justify-between">
              <span className="admin-text-gray-300">{language === 'cs' ? 'Doprava' : 'Shipping Method'}:</span>
              <span className="admin-text-white">{getShippingMethodName(orderDetails.shipping_method)}</span>
            </div>
          </div>
        </div>
      </div>
      <h4 className="admin-text-lg admin-font-semibold admin-text-white admin-mb-2">
        {language === 'cs' ? 'Položky objednávky' : 'Order Items'}
      </h4>
      <div className="admin-bg-gray-800 admin-rounded admin-p-4 admin-mb-6">
        {Array.isArray(orderDetails.items) ? (
          <div className="admin-overflow-x-auto">
            <table className="admin-min-w-full admin-table-auto">
              <thead>
                <tr className="admin-border-b admin-border-gray-700">
                  <th className="admin-px-4 admin-py-2 admin-text-left admin-text-gray-300">{language === 'cs' ? 'Produkt' : 'Product'}</th>
                  <th className="admin-px-4 admin-py-2 admin-text-right admin-text-gray-300">{language === 'cs' ? 'Cena' : 'Price'}</th>
                  <th className="admin-px-4 admin-py-2 admin-text-right admin-text-gray-300">{language === 'cs' ? 'Množství' : 'Quantity'}</th>
                  <th className="admin-px-4 admin-py-2 admin-text-right admin-text-gray-300">{language === 'cs' ? 'Celkem' : 'Total'}</th>
                </tr>
              </thead>
              <tbody>
                {/* Product items */}
                {orderDetails.items.map((item, index) => {
                  const itemName = item.translations?.cs?.name || item.translations?.en?.name || 'Produkt';
                  const price = parseFloat(item.price) || 0;
                  const quantity = item.quantity || 1;
                  const itemTotal = price * quantity;
                  return (
                    <tr key={`item-${index}`} className="admin-border-b admin-border-gray-700">
                      <td className="admin-px-4 admin-py-2 admin-text-white">{itemName}</td>
                      <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">{formatPrice(price)}</td>
                      <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">{quantity}</td>
                      <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">{formatPrice(itemTotal)}</td>
                    </tr>
                  );
                })}
                
                {/* Shipping method as a line item */}
                <tr className="admin-border-b admin-border-gray-700">
                  <td className="admin-px-4 admin-py-2 admin-text-white">
                    {language === 'cs' ? 'Doprava' : 'Shipping'}: {getShippingMethodName(orderDetails.shipping_method)}
                  </td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">
                    {(() => {
                      // Try to get shipping cost
                      const shippingMethod = shippingMethods.find(m => m.id.toString() === orderDetails.shipping_method?.toString());
                      const shippingCost = shippingMethod ? parseFloat(shippingMethod.price) : 0;
                      return formatPrice(shippingCost);
                    })()}
                  </td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">1</td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">
                    {(() => {
                      // Try to get shipping cost
                      const shippingMethod = shippingMethods.find(m => m.id.toString() === orderDetails.shipping_method?.toString());
                      const shippingCost = shippingMethod ? parseFloat(shippingMethod.price) : 0;
                      return formatPrice(shippingCost);
                    })()}
                  </td>
                </tr>
                
                {/* Payment method as a line item */}
                <tr className="admin-border-b admin-border-gray-700">
                  <td className="admin-px-4 admin-py-2 admin-text-white">
                    {language === 'cs' ? 'Platba' : 'Payment'}: {getPaymentMethodName(orderDetails.payment_method)}
                  </td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">{formatPrice(0)}</td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">1</td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-text-white">{formatPrice(0)}</td>
                </tr>
                
                {/* Total row */}
                <tr>
                  <td colSpan="3" className="admin-px-4 admin-py-2 admin-text-right admin-font-bold admin-text-white">
                    {language === 'cs' ? 'Celkem' : 'Total'}:
                  </td>
                  <td className="admin-px-4 admin-py-2 admin-text-right admin-font-bold admin-text-white">{formatPrice(orderDetails.total)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          <p className="admin-text-gray-300">{language === 'cs' ? 'Žádné položky' : 'No items'}</p>
        )}
      </div>
      <div className="admin-flex admin-justify-between admin-items-center">
        <div className="admin-flex admin-space-x-3">
          <button onClick={() => handleDownloadInvoice(currentOrder.id, 'proforma')} className="admin-btn admin-btn-primary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="admin-h-5 admin-w-5 admin-mr-2 admin-inline"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            {language === 'cs' ? 'Zálohová faktura' : 'Proforma Invoice'}
          </button>
          <button onClick={() => handleDownloadInvoice(currentOrder.id, 'final')} className="admin-btn admin-btn-secondary">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="admin-h-5 admin-w-5 admin-mr-2 admin-inline"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            {language === 'cs' ? 'Faktura' : 'Invoice'}
          </button>
        </div>
        <button onClick={() => setShowOrderDetailsModal(false)} className="admin-btn admin-btn-gray">
          {language === 'cs' ? 'Zavřít' : 'Close'}
        </button>
      </div>
    </div>
  </div>
)}
    </div>
  );
};

export default OrdersEditor;